import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { ContactShadows, PerspectiveCamera, useGLTF } from "@react-three/drei";

import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion.js";
import { CarModel } from "./CarModel.jsx";
import { StudioEnvironment } from "./StudioEnvironment.jsx";
import { measureCar } from "./carGeometry.js";
import { CAMERA_FOV, REDUCED_MOTION_PROGRESS, carFramingAt } from "./carStageConfig.js";

const CAR_MODEL_URL = "/models/hero-car.glb";

/**
 * The camera dollies in/out and rises gently as `progress` advances, always
 * recomputed from the *live* canvas aspect ratio (R3F keeps current on
 * resize) and from the loaded car's own measured bounding box — which is
 * what keeps the full 180° rotation uncropped across desktop, tablet, and
 * mobile without separate breakpoint tuning.
 */
function DynamicCamera({ progress }) {
  const camRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { size } = useThree();
  const { scene } = useGLTF(CAR_MODEL_URL);
  const car = measureCar(scene);

  useFrame(() => {
    const camera = camRef.current;
    if (!camera || size.height === 0) return;

    const p = prefersReducedMotion ? REDUCED_MOTION_PROGRESS : progress.get();
    const { position, target } = carFramingAt(p, size.width / size.height, car);

    camera.position.set(position[0], position[1], position[2]);
    camera.lookAt(target[0], target[1], target[2]);
  });

  return <PerspectiveCamera makeDefault ref={camRef} fov={CAMERA_FOV} />;
}

/**
 * Soft, blurred contact shadow anchored under the car — now that the
 * background behind the transparent canvas is a real photo of a floor
 * (rather than the old abstract gradient atmosphere), the car needs
 * something to visually ground it there instead of appearing to float.
 * Sized off the car's own measured footprint so it scales automatically
 * if the model is ever swapped, baked once (like the studio lighting
 * environment) rather than re-rendered every frame.
 */
function GroundShadow() {
  const { scene } = useGLTF(CAR_MODEL_URL);
  const car = measureCar(scene);
  const radius = Math.max(car.half.x, car.half.z) * 2.4;

  return (
    <ContactShadows
      position={[0, 0.002, 0]}
      opacity={0.55}
      scale={radius}
      blur={2.2}
      far={car.half.y * 2.4}
      resolution={512}
      color="#0a0614"
      frames={1}
    />
  );
}

/** Combines the dynamic camera, studio lighting, the ground shadow, and the
 * car itself — everything the full-bleed hero canvas needs, driven by one
 * scroll value. */
export function CarStage({ progress }) {
  return (
    <>
      <DynamicCamera progress={progress} />
      <StudioEnvironment />
      <GroundShadow />
      <CarModel progress={progress} />
    </>
  );
}
